import { useState, useCallback, useMemo } from 'react';
import { upperFirst } from 'lodash';

/**
 * Hook replacement for withShowHide from @sparefoot/react-core/decorators.
 *
 * Usage:
 *   const showHide = useShowHide('modal');
 *   // showHide.modalShowing        — boolean
 *   // showHide.onShowModal()        — set to true
 *   // showHide.onHideModal()        — set to false
 *   // showHide.onToggleModal()      — toggle
 *   // showHide.onSetModal(value)    — set to !!value
 */
export default function useShowHide(name, initialState = false) {
	const [showing, setShowing] = useState(
		typeof initialState === 'function' ? initialState : initialState,
	);

	const capitalized = upperFirst(name);

	const onShow = useCallback(() => setShowing(true), []);
	const onHide = useCallback(() => setShowing(false), []);
	const onToggle = useCallback(() => setShowing((prev) => !prev), []);
	const onSet = useCallback((value) => setShowing(!!value), []);

	return useMemo(
		() => ({
			[`${name}Showing`]: showing,
			[`onShow${capitalized}`]: onShow,
			[`onHide${capitalized}`]: onHide,
			[`onToggle${capitalized}`]: onToggle,
			[`onSet${capitalized}`]: onSet,
		}),
		[name, capitalized, showing, onShow, onHide, onToggle, onSet],
	);
}
